import { useCallback, useMemo } from 'react';
import { BlotterItem } from '../../model/blotters';
import Divider from '../common/Divider';

import { convertedQtyFormatterConfig } from '@/config/config';
import { useInstrument } from '@/utils/hooks/useInstrument';
import { convertToPips } from '@/utils/slippage';
import { useNumberFormatter } from 'react-aria';
import { useSelector } from 'react-redux';
import { cancelLimitOrder } from '../../services/OrderService';
import { selectCredentials } from '../../state/reducers/authSlice';
import Modal, { ModalHeader, ModalProps, ModalTitle } from './Modal';

interface OrderCancellationModalProps extends ModalProps {
    order1: BlotterItem;
    order2?: BlotterItem;
    onSubmit?: () => void;
}

const Order1CancellationModal = (props: OrderCancellationModalProps) => {
    const { order1, order2, handlers, onSubmit } = props;
    const { orderType } = order1;

    const credentials = useSelector(selectCredentials);
    const { pip_size } = useInstrument(order1.pair);
    const formatNumber = useNumberFormatter(convertedQtyFormatterConfig);

    const { isStopMarket, isStopLimit } = useMemo(
        () => ({
            isStopMarket: orderType === 'STOP_MARKET',
            isStopLimit: orderType === 'STOP_LIMIT'
        }),
        [orderType]
    );
    const order1PriceType = useMemo(() => (isStopMarket || isStopLimit ? 'Stop Price' : 'Limit Price'), [orderType]);
    const onCancelOrder = useCallback(() => {
        if (order1 && credentials) {
            const order1Payload = {
                orderId: order1.orderId,
                clOrdId: order1.celerClOrderId,
                origClOrdId: order1.originalCelerClOrderId,
                onBehalfOfCompId: order1.onBehalfOfCompId,
                deliverToCompId: order1.deliverToCompId,
                checkpointTimestamp: order1.checkpointTimestamp,
                onBehalfOfUsername: '',
                parentOrderId: order1.parentOrderId
            };

            if (order2) {
                const order2Payload = {
                    orderId: order2.orderId,
                    clOrdId: order2.celerClOrderId,
                    origClOrdId: order2.originalCelerClOrderId,
                    onBehalfOfCompId: order2.onBehalfOfCompId,
                    deliverToCompId: order2.deliverToCompId,
                    checkpointTimestamp: order2.checkpointTimestamp,
                    onBehalfOfUsername: '',
                    parentOrderId: order2.parentOrderId
                };

                cancelLimitOrder(order1Payload, credentials);
                cancelLimitOrder(order2Payload, credentials);
            } else {
                cancelLimitOrder(order1Payload, credentials);
            }
            handlers.close();
            // onSubmit();
        }
    }, [order1, credentials]);

    return (
        <Modal {...props} className="h-auto" size={order2 ? 'max-w-lg' : 'max-w-md'}>
            <ModalHeader>
                <ModalTitle>Order Cancellation</ModalTitle>
            </ModalHeader>
            <Divider className="bg-brand-red" />
            <div className="text-brand-text p-2 sm:p-4">
                <div className="pb-4 text-sm">
                    <div className="flex flex-col space-y-2">
                        {order2 && (
                            <div className="space-y-3">
                                <div className="flex flex-col gap-1">
                                    <h3 className="text-md font-bold pb-1 text-xs">
                                        {order1.metadata.find(({ key }) => key === 'PARENT_STRATEGY_ID')?.value}
                                    </h3>
                                    {/* TBA - Translations */}
                                    <div>Side : {order1.side}</div>
                                    <div>Symbol Name : {order1.pair}</div>
                                    <div>Duration : {order1.tif}</div>
                                </div>
                                <div className="flex gap-3">
                                    <div className="space-y-1 w-full border border-brand-neutral-600 rounded-sm p-2">
                                        <div>Order Id : {order1.orderId}</div>
                                        <div>Order Type : {order1.orderType}</div>
                                        <div>Status : {order1.status}</div>
                                        <div>
                                            {order1PriceType} :{' '}
                                            {formatNumber.format(
                                                (isStopMarket || isStopLimit ? order1.stopPrice : order1.price) || 0
                                            )}
                                        </div>
                                        <div>Quantity : {formatNumber.format(order1.qty || 0)}</div>
                                        {isStopLimit && (
                                            <>
                                                <div>Slippage (pips) : {convertToPips(order1.slippage, pip_size)}</div>
                                                <div>Stop Limit Price : {order1.stopPrice}</div>
                                            </>
                                        )}
                                    </div>
                                    <div className="space-y-1 w-full border border-brand-neutral-600 rounded-sm p-2">
                                        <div>Order Id : {order2.orderId}</div>
                                        <div>Order Type : {order2.orderType}</div>
                                        <div>Status : {order2.status}</div>
                                        <div>
                                            {order1PriceType} :{' '}
                                            {formatNumber.format(
                                                (isStopMarket || isStopLimit ? order2.stopPrice : order2.price) || 0
                                            )}
                                        </div>
                                        <div>Quantity : {formatNumber.format(order2.qty || 0)}</div>
                                        {isStopLimit && (
                                            <>
                                                <div>Slippage (pips) : {convertToPips(order2.slippage, pip_size)}</div>
                                                <div>Stop Limit Price : {order2.stopPrice}</div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                        {!order2 && (
                            <div className="space-y-1">
                                <h3 className="text-md font-bold pb-1">Order Details</h3>
                                <div>Symbol Name : {order1.pair}</div>
                                <div>Side : {order1.side}</div>
                                <div>Order Type : {order1.orderType}</div>
                                <div>Order Id : {order1.orderId}</div>
                                <div>Status : {order1.status}</div>
                                <div>Duration : {order1.tif}</div>
                                <div>
                                    {order1PriceType} :{' '}
                                    {formatNumber.format(
                                        (isStopMarket || isStopLimit ? order1.stopPrice : order1.price) || 0
                                    )}
                                </div>
                                <div>Quantity : {formatNumber.format(order1.qty || 0)}</div>
                                {isStopLimit && (
                                    <>
                                        <div>Slippage (pips) : {convertToPips(order1.slippage, pip_size)}</div>
                                        <div>Stop Limit Price : {order1.stopPrice}</div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <div className="text-sm">
                    {order2
                        ? 'Are you sure you want to cancel both orders?'
                        : 'Are you sure you want to cancel this order?'}
                </div>
            </div>
            <Divider className="bg-brand-red" />
            <div className="text-brand-text p-2 sm:p-4 text-sm flex space-x-2">
                <button
                    className="rounded-md p-2 px-4 bg-brand-neutral-600 hover:bg-brand-neutral-500"
                    onClick={handlers.close}>
                    Back
                </button>
                <button className="rounded-md p-2 px-4 bg-brand-red hover:bg-brand-red-light" onClick={onCancelOrder}>
                    {order2 ? 'Cancel Both Orders' : 'Cancel Order'}
                </button>
            </div>
        </Modal>
    );
};

export default Order1CancellationModal;
